import { ReactNode } from 'react'
import { Input } from '@/components'
import { TableContainer, TableHeader, TableBody, TableRow, TableCell, TableInputContainer } from './CenaTable.styles'

export interface CenaTableColumn {
  key: string
  title: string
  width?: number
  align?: 'left' | 'center' | 'right'
  isGroupColumn?: boolean
}

export interface CenaTableData {
  id: string
  [key: string]: any
}

export interface GroupedCenaTableData extends CenaTableData {
  groupDisplay?: string
  rowSpan?: number
}

interface CenaTableProps {
  columns: CenaTableColumn[]
  data: CenaTableData[]
  onCellChange?: (id: string, field: string, value: string) => void
  onGroupedCellChange?: (id: string, label: string, value: string) => void
}

export function CenaTable({ columns, data, onCellChange, onGroupedCellChange }: CenaTableProps) {
  const isGroupedTable = columns.some((col) => col.isGroupColumn)

  const renderCell = (item: CenaTableData, column: CenaTableColumn): ReactNode => {
    if (column.key === 'value') {
      return (
        <TableInputContainer>
          <Input
            type="number"
            value={item[column.key] || ''}
            onChange={(value) => {
              if (isGroupedTable && onGroupedCellChange) {
                onGroupedCellChange(item.id, item.label, String(value))
              } else if (onCellChange) {
                onCellChange(item.id, column.key, String(value))
              }
            }}
          />
        </TableInputContainer>
      )
    }

    return item[column.key]
  }

  const renderGroupedTable = () => {
    const groupedData = data as GroupedCenaTableData[]
    const result: ReactNode[] = []
    const groups = groupedData.reduce(
      (acc, item) => {
        if (!acc[item.id]) {
          acc[item.id] = []
        }
        acc[item.id].push(item)
        return acc
      },
      {} as Record<string, GroupedCenaTableData[]>,
    )

    let rowIndex = 0
    let groupIndex = 0
    Object.entries(groups).forEach(([, groupItems]) => {
      const isEvenGroup = groupIndex % 2 === 0

      groupItems.forEach((item, itemIndex) => {
        const isFirstInGroup = itemIndex === 0

        result.push(
          <TableRow key={`row-${rowIndex}`} $isGroupRow={isEvenGroup}>
            {columns.map((column) => {
              if (column.isGroupColumn) {
                if (isFirstInGroup) {
                  return (
                    <TableCell
                      key={`group-${column.key}-${rowIndex}`}
                      $width={column.width}
                      $align={column.align}
                      rowSpan={groupItems.length}
                      style={{ verticalAlign: 'middle', fontWeight: 500 }}>
                      {item.groupDisplay}
                    </TableCell>
                  )
                }
                return null
              }

              return (
                <TableCell key={`cell-${column.key}-${rowIndex}`} $width={column.width} $align={column.align}>
                  {renderCell(item, column)}
                </TableCell>
              )
            })}
          </TableRow>,
        )
        rowIndex++
      })
      groupIndex++
    })

    return result
  }

  const renderSimpleTable = () => {
    return data.map((item, index) => (
      <TableRow key={`row-${index}`} $isGroupRow={false}>
        {columns.map((column) => (
          <TableCell key={`cell-${column.key}-${index}`} $width={column.width} $align={column.align}>
            {renderCell(item, column)}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <TableContainer>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <TableHeader>
          <tr>
            {columns.map((column) => (
              <TableCell
                key={`header-${column.key}`}
                as="th"
                $width={column.width}
                $align={column.align}
                $isHeader
                style={{ padding: '12px 16px', fontWeight: 600 }}>
                {column.title}
              </TableCell>
            ))}
          </tr>
        </TableHeader>
        <TableBody as="tbody">{isGroupedTable ? renderGroupedTable() : renderSimpleTable()}</TableBody>
      </table>
    </TableContainer>
  )
}
